import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  PaymentMethodSetupHandle,
  SavedPaymentMethod,
} from "components/payment/PaymentMethodSetup/types";
import type { SetupAttempt } from "components/payment/PaymentMethodSetup/types";
import type { Contact } from "types";
import { useAuth, useCurrentUser, useOperator } from "globals/hooks";
import type { ReservationFormSnapshot, ReservationSetupReturn } from "./reservationSetupReturn";
import type { ProfileSetupReturn } from "components/profile/paymentMethodSetupReturn";
import {
  clearSetupKey,
  getSetupKey,
  readSetupKey,
  retainSetupKey,
} from "components/payment/PaymentMethodSetup/setupAttemptKey";

export type PreparedReservationGuest = {
  contact: Pick<
    Contact,
    | "id"
    | "firstName"
    | "lastName"
    | "email"
    | "mobilePhone"
    | "mobilePhoneInternational"
    | "phoneCountryCode"
    | "phoneCountryDialCode"
    | "phoneCountryName"
    | "phoneCountryFormat"
  >;
  preparationProof: string;
  detailsKey: string;
  preparedAt: number;
};

export type ReservationPaymentSetup = {
  ownerKey: string;
  enabled: boolean;
  checkoutContext: string;
  attemptKey: string;
  restoredForm?: ReservationFormSnapshot;
  guest?: PreparedReservationGuest;
  attempt?: SetupAttempt;
  result?: SavedPaymentMethod;
  resume?: Pick<SetupAttempt, "setupIntentId" | "clientSecret">;
  addCardReturn?: ProfileSetupReturn;
};

const useSetupStore = (setupReturn?: ReservationSetupReturn) => {
  const { operator } = useOperator();
  const { authStage } = useAuth();
  const currentUser = useCurrentUser();
  const ownerKey = JSON.stringify([operator.id, authStage, currentUser?.id]);
  const ownerKeyRef = useRef(ownerKey);
  const setupReturnRef = useRef(setupReturn);
  ownerKeyRef.current = ownerKey;
  const [state, setState] = useState<ReservationPaymentSetup | null>(null);
  const stateRef = useRef(state);
  const pendingRef = useRef<{
    ownerKey: string;
    promise: Promise<PreparedReservationGuest>;
  } | null>(null);
  const elementRef = useRef<PaymentMethodSetupHandle>(null);
  const update = useCallback((next: ReservationPaymentSetup) => {
    if (next.ownerKey !== ownerKeyRef.current) return;
    stateRef.current = next;
    setState(next);
  }, []);
  useEffect(() => {
    if (
      !operator.id ||
      authStage === "pending" ||
      (authStage === "authenticated" && !currentUser?.id)
    )
      return;
    if (stateRef.current?.ownerKey === ownerKey) return;
    const restored = setupReturnRef.current;
    setupReturnRef.current = undefined;
    const checkoutContext = getSetupKey(
      JSON.stringify(["reservation", ownerKey])
    );
    update(
      restored?.state.ownerKey === ownerKey
        ? restored.state
        : {
            ownerKey,
            enabled: operator.enablePaymentElement === true,
            checkoutContext,
            attemptKey: getSetupKey(
              JSON.stringify(["reservation-attempt", ownerKey, checkoutContext])
            ),
          }
    );
  }, [
    ownerKey,
    authStage,
    currentUser?.id,
    operator.id,
    operator.enablePaymentElement,
    update,
  ]);
  useEffect(() => {
    if (!state || state.ownerKey !== ownerKey) return;
    const scope = JSON.stringify(["reservation", ownerKey]);
    const attemptScope = JSON.stringify([
      "reservation-attempt", ownerKey, state.checkoutContext,
    ]);
    for (const [keyScope, key] of [
      [scope, state.checkoutContext],
      [attemptScope, state.attemptKey],
    ]) {
      if (state.result) clearSetupKey(keyScope);
      else if (readSetupKey(keyScope) !== key) retainSetupKey(keyScope, key);
    }
  }, [state, ownerKey]);
  return {
    state: state?.ownerKey === ownerKey ? state : null,
    stateRef,
    ownerKeyRef,
    update,
    pendingRef,
    elementRef,
  };
};

const ReservationPaymentSetupContext = createContext<ReturnType<
  typeof useSetupStore
> | null>(null);

export const ReservationPaymentSetupProvider = ({
  children,
  setupReturn,
}: React.PropsWithChildren<{ setupReturn?: ReservationSetupReturn }>) => {
  const store = useSetupStore(setupReturn);
  return (
    <ReservationPaymentSetupContext.Provider value={store}>
      {children}
    </ReservationPaymentSetupContext.Provider>
  );
};

export const useReservationPaymentSetupStore = () => {
  const store = useContext(ReservationPaymentSetupContext);
  if (!store)
    throw new Error("Reservation checkout requires its setup provider");
  return store;
};
